/**
 * The HUD's look, in one place (2026-09-01, restyled 2026-09-02).
 *
 * Originally: "All the assets in the HUD should match the game's art style —
 * more cartoonish, similar to the characters and maps." The HUD had no art at
 * all — flat black boxes with hairline `ring-1` borders — so it got fat ink
 * outlines, toffee-brown wood and hard offset shadows.
 *
 * That over-corrected, and the user said so about the identical treatment in
 * the hub on 2026-09-02: *"it turned out a bit too cartoonish. I want to
 * preserve the game's overall visual style — matching our assets — without
 * turning it into a full-blown cartoon."* The hub was retuned then; this was
 * not, which left the screen you spend the most time on as the last place
 * still running the rejected look. Now it matches: lit stone, warm hairline
 * edges, soft cast shadows — the same parts as `styles/app.css`.
 *
 * The rules that make it read as the same game as the world:
 *
 * - **Lit, not outlined.** One hairline of warm light on the top edge and a
 *   soft shadow beneath. A 3px ink outline is a drawing of a panel.
 * - **Dark stone panels, bright fills.** The dungeon floor is pale cream, so
 *   a parchment panel would vanish into it. Dark warm stone keeps contrast
 *   over the world while staying warm rather than grey, and it lets the bars
 *   be the brightest thing in the frame — which is what a player is scanning
 *   for. That part was right the first time and is unchanged.
 * - **Bar colours are untouched.** Green/amber/red health and the
 *   per-resource tints are gameplay information read at a glance under
 *   pressure; they are the one thing on screen that should not be harmonised
 *   into the palette.
 *
 * Colours are exported as named constants rather than written inline at each
 * use because the tests assert on them: a test that asserts `bg-emerald-500`
 * is really asserting "healthy is a different colour from hurt", and pinning
 * that to a literal makes every palette change look like a broken test.
 */

/**
 * The dark edge under HUD text and glyphs.
 *
 * No longer an outline drawn *around* panels — that was the cartoon tell —
 * but still the shadow colour that keeps cream lettering and bright icons
 * legible over a pale dungeon floor.
 *
 * Written out literally at each use below rather than interpolated: Tailwind v4
 * extracts class names by scanning source text, so a `border-[${INK}]` template
 * produces the right string at runtime and no matching CSS rule at build time.
 */
export const INK = '#241309';

/** Health, by how much is left. Green healthy, amber hurt, red about to die. */
export const HP_TINT = {
  healthy: 'bg-[#5ec24a]',
  hurt: 'bg-[#f0a92c]',
  critical: 'bg-[#e0483a]',
} as const;

/** Stamina — a lime distinct from the health green, since they sit adjacent. */
export const STAMINA_TINT = 'bg-[#b9e34a]';

/** One per resource kind, so rage never reads as mana at a glance. */
export const RESOURCE_TINT: Record<string, string> = {
  MANA: 'bg-[#47a8f5]',
  RAGE: 'bg-[#ef5a3a]',
  ENERGY: 'bg-[#f5cf3f]',
};

/** The cartoon glyph that names each bar, so colour is never the only cue. */
export const RESOURCE_GLYPH: Record<string, string> = {
  MANA: 'droplet',
  RAGE: 'flame',
  ENERGY: 'bolt',
};

export const glyphUrl = (name: string): string => `/assets/ui/hud/${name}.webp`;
export const portraitUrl = (characterClass: string): string => `/assets/ui/lobby/portrait-${characterClass}.png`;

/**
 * A stone slab — `frame-pewter` from `styles/app.css`, the same part the hub
 * panels are built from. Named as a constant here because the HUD builds its
 * DOM in TypeScript rather than in template literals, and because the tests
 * assert on it.
 */
export const PLAQUE = 'frame-pewter';

/** The same slab at party-row scale — the utility is size-agnostic. */
export const PLAQUE_SMALL = 'frame-pewter';

/** Cream text with a dark edge, so it survives sitting over a bright bar. */
export const LABEL = 'text-[#ffeccc] [text-shadow:0_1px_0_#241309,0_0_3px_#241309]';

export interface ChunkyBar {
  wrap: HTMLDivElement;
  fill: HTMLDivElement;
  label: HTMLDivElement;
}

/**
 * A rounded capsule bar cut into stone, with a gloss highlight on the fill.
 *
 * The gloss is a real child rather than a pseudo-element so it can sit above
 * the fill and below the label without any z-index bookkeeping, and so the
 * whole thing survives being built in a jsdom test.
 *
 * @param height  Tailwind height class — the hierarchy between bars is carried
 *                by thickness, not just colour.
 * @param glyph   Optional cartoon glyph pinned to the left cap, naming the bar.
 */
export function chunkyBar(height: string, glyph?: string): ChunkyBar {
  // Two elements, deliberately: the track clips its fill and gloss to the
  // capsule, and the glyph has to hang outside that clip to overlap the cap.
  // One element cannot do both — `overflow-hidden` would eat the glyph.
  const wrap = document.createElement('div');
  wrap.className = 'relative w-full';

  const track = document.createElement('div');
  track.className = `meter w-full ${height}`;

  const fill = document.createElement('div');
  // Not the `meter-fill` utility: this one animates in 150ms rather than the
  // 300ms a hub meter uses — a health bar that lags a hit is misinformation.
  fill.className = 'absolute inset-y-0 left-0 w-0 rounded-full transition-[width] duration-150 ease-out';

  const gloss = document.createElement('div');
  gloss.className = 'absolute inset-x-0 top-0 h-1/2 rounded-t-full pointer-events-none '
    + 'bg-[linear-gradient(180deg,rgba(255,255,255,0.35),rgba(255,255,255,0))]';

  const label = document.createElement('div');
  label.className = `absolute inset-0 flex items-center justify-center ${LABEL} font-semibold tabular-nums`;

  track.append(fill, gloss, label);
  wrap.appendChild(track);

  if (glyph) {
    const icon = document.createElement('img');
    icon.src = glyphUrl(glyph);
    icon.alt = '';
    icon.draggable = false;
    // Rides the left cap rather than sitting beside the bar, so the bar keeps
    // its full width inside a 13rem panel and the glyph still marks the end
    // that stays put as the fill drains.
    icon.className = 'absolute -left-1 top-1/2 -translate-y-1/2 h-[165%] w-auto select-none '
      + 'drop-shadow-[0_1px_0_#241309] pointer-events-none';
    wrap.appendChild(icon);
  }

  return { wrap, fill, label };
}

/** A round class portrait in a warm ring — the lobby art, reused in play. */
export function portraitDisc(sizeClass: string): { wrap: HTMLDivElement; img: HTMLImageElement } {
  const wrap = document.createElement('div');
  wrap.className = `relative shrink-0 ${sizeClass} rounded-full border-2 border-[#514132] `
    + 'bg-[radial-gradient(circle_at_50%_35%,#f0d6ac,#c99a63)] overflow-hidden '
    + 'shadow-[inset_0_1px_0_rgba(255,222,176,0.18),0_2px_10px_rgba(0,0,0,0.5)]';
  const img = document.createElement('img');
  img.alt = '';
  img.draggable = false;
  img.className = 'w-full h-full object-cover select-none';
  wrap.appendChild(img);
  return { wrap, img };
}
